import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { endOfMonth, format, getDay, startOfMonth } from 'date-fns';
import { vi } from 'date-fns/locale/vi';
import {
  Box,
  CircularProgress,
  FormHelperText,
  HelpTip,
  Stack,
  Typography,
} from '@/shared/ui';
import type {
  AppointmentDateBlockReason,
  BookingOptionsResult,
} from '@/modules/scheduling/api/availability.api';
import { typography } from '@/shared/theme/tokens';
import { useBookingOptions } from '@/modules/scheduling/hooks/useBookingOptions';
import { appointmentCalendarNeedsDateFetch } from '../utils/calendarDatePolicy';

/** Match the 40px month arrows so weekday labels and day cells fill the wide calendar. */
const CALENDAR_DAY_SIZE = 40;

/** Week starts Monday, so the 7th header cell is Sunday (CN). */
const sundayHeaderSx = {
  '& .MuiDayCalendar-weekDayLabel:nth-of-type(7)': {
    color: 'error.main',
  },
} as const;

const AppointmentCalendarDay = (props: PickersDayProps) => {
  const isSunday = getDay(props.day) === 0;
  return (
    <PickersDay
      {...props}
      sx={
        isSunday && !props.selected
          ? {
              color: 'error.main',
              '&.Mui-disabled': { color: 'error.main' },
            }
          : undefined
      }
    />
  );
};

export interface AppointmentSchedulePanelProps {
  clinicId: string;
  staffId: string;
  date: Date | null;
  /** Resolved org/clinic policy. Only `block` (or a lead/advance window) loads days. */
  bookingPolicy?: BookingOptionsResult['policy'];
  onDateChange: (date: Date | null) => void;
  disabled?: boolean;
  dateError?: boolean;
  dateHelperText?: React.ReactNode;
  disablePast?: boolean;
  /**
   * When true (edit mode), keep the current date even if availability
   * has not loaded yet or that day is no longer bookable.
   * Newly picked dates still must be bookable.
   */
  preserveSelection?: boolean;
  excludeAppointmentId?: string;
}

const AppointmentSchedulePanel: React.FC<AppointmentSchedulePanelProps> = ({
  clinicId,
  staffId,
  date,
  bookingPolicy = null,
  onDateChange,
  disabled = false,
  dateError = false,
  dateHelperText,
  disablePast = true,
  preserveSelection = false,
  excludeAppointmentId,
}) => {
  const { t } = useTranslation(['appointment']);
  /** Phase C: clinic unlocks dates; doctor narrows to their working days when set. */
  const canLoadDays = Boolean(clinicId);
  const selectedDateKey = date ? format(date, 'yyyy-MM-dd') : null;

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => date ?? new Date());

  // Keep calendar month in sync when date is prefilled from outside.
  useEffect(() => {
    if (!date) return;
    setVisibleMonth((prev) =>
      prev.getFullYear() === date.getFullYear() && prev.getMonth() === date.getMonth()
        ? prev
        : date
    );
  }, [date]);

  const monthFrom = format(startOfMonth(visibleMonth), 'yyyy-MM-dd');
  const monthTo = format(endOfMonth(visibleMonth), 'yyyy-MM-dd');

  const needsDateFetch = appointmentCalendarNeedsDateFetch(bookingPolicy, staffId);
  const scopeDatesToDoctor = bookingPolicy?.schedule?.staffSchedule === 'block';

  const daysQuery = useBookingOptions(
    canLoadDays && needsDateFetch
      ? {
          clinicId,
          doctorId: scopeDatesToDoctor ? staffId || undefined : undefined,
          from: monthFrom,
          to: monthTo,
          excludeAppointmentId,
        }
      : null
  );

  const bookableDates = useMemo(() => {
    const next = new Set<string>();
    for (const entry of daysQuery.data?.dates ?? []) {
      if (entry.ok) next.add(entry.date);
    }
    return next;
  }, [daysQuery.data?.dates]);

  const dayReasonByDate = useMemo(() => {
    const next = new Map<string, AppointmentDateBlockReason>();
    for (const entry of daysQuery.data?.dates ?? []) {
      if (!entry.ok && entry.reason) {
        next.set(entry.date, entry.reason);
      }
    }
    return next;
  }, [daysQuery.data?.dates]);

  const loadingDays = daysQuery.isFetching;
  const daysReady = daysQuery.isFetched;
  const daysError = daysQuery.isError ? t('schedule.loadDaysFailed') : null;

  // Validate date only after days for this context have loaded — avoids wiping
  // calendar prefills while bookableDates is still empty / stale.
  useEffect(() => {
    if (!needsDateFetch || preserveSelection) return;
    if (!date || !canLoadDays) return;
    if (loadingDays || !daysReady) return;
    const datePart = format(date, 'yyyy-MM-dd');
    if (!bookableDates.has(datePart)) {
      onDateChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid loop on inline onDateChange
  }, [
    needsDateFetch,
    canLoadDays,
    clinicId,
    staffId,
    loadingDays,
    daysReady,
    bookableDates,
    date,
    preserveSelection,
  ]);

  const selectedDateReason = date
    ? dayReasonByDate.get(format(date, 'yyyy-MM-dd'))
    : undefined;

  const clinicDoctorHint = t('schedule.selectClinicDoctorHint');

  const calendarHint = daysError
    ? daysError
    : !canLoadDays
      ? t('schedule.selectClinicHint')
      : selectedDateReason
        ? t(`schedule.reasons.${selectedDateReason}` as any)
        : dateHelperText;

  return (
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {t('schedule.pickDate')}
          <HelpTip title={clinicDoctorHint} />
        </Typography>
        <Box
          sx={{
            border: 1,
            borderColor: dateError || daysError ? 'error.main' : 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
            position: 'relative',
          }}
        >
          {loadingDays ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.55)',
                gap: 1,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('schedule.loadingDays')}
              </Typography>
            </Box>
          ) : null}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
          <DateCalendar
            value={date}
            referenceDate={visibleMonth}
            onChange={(next) => onDateChange(next)}
            onMonthChange={(month) => setVisibleMonth(month)}
            disablePast={disablePast}
            dayOfWeekFormatter={(day) => format(day, 'EEEEE', { locale: vi })}
            slots={{ day: AppointmentCalendarDay }}
            slotProps={{
              calendarHeader: { format: "'Tháng' M, yyyy" },
            }}
            shouldDisableDate={(day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              if (preserveSelection && selectedDateKey && dayKey === selectedDateKey) {
                return false;
              }
              if (!canLoadDays) return true;
              if (!needsDateFetch) return false;
              if (daysError) return true;
              return !bookableDates.has(dayKey);
            }}
            sx={{
              width: '100%',
              maxHeight: 'none',
              '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
                justifyContent: 'space-between',
                pl: 3,
                pr: 1.5,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                width: CALENDAR_DAY_SIZE,
                height: CALENDAR_DAY_SIZE,
                m: 0,
                fontSize: typography.size.lg,
                fontWeight: typography.weight.medium,
              },
              '& .MuiPickersDay-root': {
                width: CALENDAR_DAY_SIZE,
                height: CALENDAR_DAY_SIZE,
                my: '2px',
                mx: 0,
                fontSize: typography.size.lg,
              },
              '& .MuiPickersSlideTransition-root': {
                minHeight: (CALENDAR_DAY_SIZE + 4) * 6,
              },
              ...sundayHeaderSx,
            }}
          />
          </LocalizationProvider>
        </Box>
        {calendarHint ? (
          <FormHelperText error={Boolean(dateError || daysError || selectedDateReason)}>
            {calendarHint}
          </FormHelperText>
        ) : null}
      </Box>
    </Stack>
  );
};

export default React.memo(AppointmentSchedulePanel);
